import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import { Layout, ConfigProvider, Drawer } from "antd";
import { Content, Header } from "antd/es/layout/layout";
import HeaderComponent from "./HeaderComponent";
import LeftSiderComponent from "./LeftSiderComponent";
import RightSiderComponent from "./RightSiderComponent";
import MainContent from "./MainContent";
import "./App.css";

interface AppLayoutProps {
  children: React.ReactNode;
  prompt: string;
  setPrompt: (prompt: string) => void;
  model: string;
  setModel: (model: string) => void;
  compareMode: boolean;
  setCompareMode: (enabled: boolean) => void;
  modelA: string;
  setModelA: (model: string) => void;
  modelB: string;
  setModelB: (model: string) => void;
}

const AppLayout: React.FC<AppLayoutProps> = ({
  children,
  prompt,
  setPrompt: handlePromptChange,
  model,
  setModel: handleModelChange,
  compareMode,
  setCompareMode,
  modelA,
  setModelA,
  modelB,
  setModelB,
}) => {
  const [leftSiderVisible, setLeftSiderVisible] = useState(false);
  const [rightSiderVisible, setRightSiderVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Track touch start positions
  const touchStartX = useRef(0);
  const touchThreshold = 50; // Minimum distance to trigger swipe

  // Handle window resize to detect mobile/desktop
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);

      // On desktop, siders are always visible
      if (!mobile) {
        setLeftSiderVisible(false);
        setRightSiderVisible(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Handle touch events for swiping to reveal siders
  useEffect(() => {
    const handleTouchStart = (e: TouchEvent) => {
      touchStartX.current = e.touches[0].clientX;
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (!isMobile) return;

      const touchEndX = e.changedTouches[0].clientX;
      const screenWidth = window.innerWidth;
      const deltaX = touchEndX - touchStartX.current;

      // Right to left swipe near right edge (show right sider)
      if (touchStartX.current > screenWidth - 20 && deltaX < -touchThreshold) {
        setRightSiderVisible(true);
      }

      // Left to right swipe near left edge (show left sider)
      if (touchStartX.current < 20 && deltaX > touchThreshold) {
        setLeftSiderVisible(true);
      }
    };

    document.addEventListener("touchstart", handleTouchStart);
    document.addEventListener("touchend", handleTouchEnd);

    return () => {
      document.removeEventListener("touchstart", handleTouchStart);
      document.removeEventListener("touchend", handleTouchEnd);
    };
  }, [isMobile]);

  // Handler for tapping screen edges
  const handleScreenEdgeTap = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isMobile) return;

    const clientX =
      "touches" in e
        ? (e as React.TouchEvent).touches[0].clientX
        : (e as React.MouseEvent).clientX;

    const screenWidth = window.innerWidth;

    if (clientX < 20) {
      setLeftSiderVisible(true);
    } else if (clientX > screenWidth - 20) {
      setRightSiderVisible(true);
    }
  };

  return (
    <Layout
      style={{ maxHeight: "calc(100vh)", height: "calc(100vh)" }}
      onClick={handleScreenEdgeTap}
      onTouchStart={handleScreenEdgeTap}>
      <Header className="Header">
        <HeaderComponent />
      </Header>
      <Layout style={{ height: "100%" }}>
        {isMobile ? (
          // Mobile layout with drawers
          <>
            <Drawer
              title="Configuration"
              placement="left"
              closable={true}
              onClose={() => setLeftSiderVisible(false)}
              open={leftSiderVisible}
              width="100%"
              styles={{
                body: {
                  padding: 0,
                  overflow: "hidden",
                  width: "100%",
                  maxWidth: "400px",
                },
                header: { padding: "12px 16px" },
                content: {
                  width: "100%",
                  overflow: "hidden",
                  maxWidth: "400px",
                },
                wrapper: { width: "100%", maxWidth: "400px" },
              }}>
              <LeftSiderComponent
                prompt={prompt}
                setPrompt={handlePromptChange}
                model={model}
                setModel={handleModelChange}
                compareMode={compareMode}
                setCompareMode={setCompareMode}
                modelA={modelA}
                setModelA={setModelA}
                modelB={modelB}
                setModelB={setModelB}
              />
            </Drawer>

            <Drawer
              title="Instructions"
              placement="right"
              closable={true}
              onClose={() => setRightSiderVisible(false)}
              open={rightSiderVisible}
              width="100%"
              styles={{
                body: {
                  padding: 0,
                  overflow: "hidden",
                  width: "100%",
                  maxWidth: "400px",
                },
                header: { padding: "12px 16px" },
                content: {
                  width: "100%",
                  overflow: "hidden",
                  maxWidth: "400px",
                },
                wrapper: { width: "100%", maxWidth: "400px" },
              }}>
              <RightSiderComponent />
            </Drawer>

            {/* Edge indicators for mobile users */}
            <div className="edge-indicator-left" />
            <div className="edge-indicator-right" />
          </>
        ) : (
          // Desktop layout with static sider
          <LeftSiderComponent
            prompt={prompt}
            setPrompt={handlePromptChange}
            model={model}
            setModel={handleModelChange}
            compareMode={compareMode}
            setCompareMode={setCompareMode}
            modelA={modelA}
            setModelA={setModelA}
            modelB={modelB}
            setModelB={setModelB}
          />
        )}

        <Content
          style={{
            background: "#fff",
            overflow: "hidden",
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}>
          {children}
        </Content>

        {!isMobile && <RightSiderComponent />}
      </Layout>
    </Layout>
  );
};

const App: React.FC = () => {
  const [prompt, setPrompt] = useState("");
  const [model, setModel] = useState("breeze");
  const [compareMode, setCompareMode] = useState(false);
  const [modelA, setModelA] = useState("breeze");
  const [modelB, setModelB] = useState("breeze");

  const handlePromptChange = useCallback((newPrompt: string) => {
    setPrompt(newPrompt);
  }, []);

  const handleModelChange = useCallback((newModel: string) => {
    setModel(newModel);
  }, []);

  // Define your custom theme
  const customTheme = {
    token: {
      colorPrimary: "#21b892",
      colorSecondary: "#ffa658",
      // Add more color customizations here
    },
  };

  return (
    <ConfigProvider theme={customTheme}>
      <Routes>
        <Route
          path="*"
          element={
            <AppLayout
              prompt={prompt}
              setPrompt={handlePromptChange}
              model={model}
              setModel={handleModelChange}
              compareMode={compareMode}
              setCompareMode={setCompareMode}
              modelA={modelA}
              setModelA={setModelA}
              modelB={modelB}
              setModelB={setModelB}>
              <MainContent
                prompt={prompt}
                model={model}
                compareMode={compareMode}
                modelA={modelA}
                modelB={modelB}
              />
            </AppLayout>
          }
        />
      </Routes>
    </ConfigProvider>
  );
};

export default () => (
  <Router>
    <App />
  </Router>
);
